/**
 * React Query hooks for LPA Audits.
 *
 * Audits are factory-scoped (tenant_id + factory_id).
 * Each audit has an owner (profile), a factory location scope (area/line/station),
 * and a lifecycle status (draft → active → archived).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/core/lib/queryKeys';
import { useFactory } from '@/core/hooks/useFactory';
import { useTenant } from '@/core/hooks/useTenant';
import { useAuth } from '@/core/AuthContext';
import { useToast } from '@/shared/ui/use-toast';
import { useLanguage } from '@/core/LanguageContext';
import {
  fetchAudits,
  fetchAuditById,
  createAudit,
  updateAudit,
  deleteAudit,
} from '../api/audits';
import type { CreateAuditInput, UpdateAuditInput } from '../types';

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Fetch all audits for the current factory with owner + schedule count. */
export function useAudits() {
  const { selectedFactoryId } = useFactory();

  return useQuery({
    queryKey: queryKeys.modLpaAudits(selectedFactoryId),
    queryFn: () => fetchAudits(selectedFactoryId!),
    enabled: !!selectedFactoryId,
  });
}

/** Fetch a single audit by ID with owner + schedule count. */
export function useAuditById(auditId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.modLpaAuditDetail(auditId ?? null),
    queryFn: () => fetchAuditById(auditId!),
    enabled: !!auditId,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateAudit() {
  const queryClient = useQueryClient();
  const { selectedFactoryId } = useFactory();
  const { selectedTenantId } = useTenant();
  const { profile } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (
      input: Omit<CreateAuditInput, 'tenant_id' | 'factory_id' | 'created_by'>,
    ) =>
      createAudit({
        ...input,
        tenant_id: selectedTenantId!,
        factory_id: selectedFactoryId!,
        created_by: profile?.id ?? null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaAudits(selectedFactoryId),
      });
      toast({
        title: t('mod_lpa.audits.createSuccess', 'Audit created'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.audits.createError', 'Failed to create audit'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateAudit() {
  const queryClient = useQueryClient();
  const { selectedFactoryId } = useFactory();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({
      auditId,
      input,
    }: {
      auditId: string;
      input: UpdateAuditInput;
    }) => updateAudit(auditId, input),
    onSuccess: (_data, { auditId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaAudits(selectedFactoryId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaAuditDetail(auditId),
      });
      toast({
        title: t('mod_lpa.audits.updateSuccess', 'Audit updated'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.audits.updateError', 'Failed to update audit'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteAudit() {
  const queryClient = useQueryClient();
  const { selectedFactoryId } = useFactory();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (auditId: string) => deleteAudit(auditId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaAudits(selectedFactoryId),
      });
      toast({
        title: t('mod_lpa.audits.deleteSuccess', 'Audit deleted'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.audits.deleteError', 'Failed to delete audit'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
